import type { ReactNode } from 'react';
import {
  sumEntries,
  taxKindOf,
  type CashFlowEntry as CoreEntry,
  type MoneySum,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { summarizeLedgerScope } from '@/lib/ledgerScopeHeader';
import {
  formatEur,
  formatNumCompact,
  isGhostEntry,
  PRIORITY_CLS,
  PRIORITY_LABEL,
  type CashFlowEntry,
  type MoneyMode,
} from '@/lib/cashFlow';
import { LINE_META } from '../categoryMeta';
import { settlementView } from '@/lib/settlementDetail';
import { SettlementDetail } from '../settlement/SettlementDetail';
import { relatedDocCells } from '@/lib/relatedDocuments';
import {
  CERTAINTY_GLYPH,
  CERTAINTY_LABEL,
  DOC_TYPE_LABEL,
  fmtDayMonth,
  type HoverPos,
} from './model';

/** Clamp a hover card so it stays on-screen near the cursor. */
function place(pos: HoverPos, w: number, h: number): { left: number; top: number } {
  return {
    left: Math.max(8, Math.min(pos.x + 14, window.innerWidth - w)),
    top: Math.max(8, Math.min(pos.y + 14, window.innerHeight - h)),
  };
}

function KindBadge({ isIncome }: { isIncome: boolean }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1 text-[9px] font-medium uppercase tracking-wide',
        isIncome ? 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]' : 'bg-[hsl(var(--cost))]/15 text-[hsl(var(--cost))]',
      )}
    >
      {isIncome ? 'laekumine' : 'makse'}
    </span>
  );
}

/**
 * Installment (group) hover: the original due day, the total (finance-core
 * `sumEntries` — no component maths), the payment plan parts with their per-part
 * status, and any remainder still to be scheduled. Shown for grouped entries.
 */
export function GroupHoverCard({
  pos,
  entries,
  today,
}: {
  pos: HoverPos;
  entries: CashFlowEntry[];
  today: string;
}) {
  const e = entries.find((x) => x.id === pos.id);
  if (!e || e.groupId === null) return null;

  const members = entries.filter((x) => x.groupId === e.groupId);
  const ghost = members.find(isGhostEntry);
  const parts = members
    .filter((m) => !isGhostEntry(m))
    .sort((a, b) => (a.expectedDate < b.expectedDate ? -1 : a.expectedDate > b.expectedDate ? 1 : 0));
  const originIso = ghost?.originExpectedDate ?? e.originExpectedDate;
  const total = sumEntries(members as CoreEntry[]).gross;
  const remaining = ghost ? ghost.money.gross : 0;
  const isIncome = e.kind === 'income';
  const partStatus = (m: CashFlowEntry): string =>
    m.actualDate !== null ? (isIncome ? 'laekunud' : 'makstud') : m.expectedDate > today ? 'planeeritud' : 'ootel';

  const { left, top } = place(pos, 268, 180);

  return (
    <div
      className="pointer-events-none fixed z-50 w-64 rounded-md border border-border bg-card/95 px-3 py-2 text-[11px] shadow-xl backdrop-blur"
      style={{ left, top }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-1">
        <span className="truncate font-semibold text-foreground">{e.party}</span>
        <KindBadge isIncome={isIncome} />
      </div>
      <dl className="mt-1 space-y-0.5">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Algne tähtaeg</dt>
          <dd className="tabular-nums text-foreground/90">{originIso ? fmtDayMonth(originIso) : '—'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Kogusumma (bruto)</dt>
          <dd className="tabular-nums font-semibold text-foreground">{formatEur(total)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Makseplaan</dt>
          <dd className="text-right text-foreground/90">
            {parts.length > 0
              ? `tükeldatud · ${parts.length} osa${remaining > 0 ? ' + jääk' : ''}`
              : 'edasi lükatud (terve summa)'}
          </dd>
        </div>
      </dl>
      {parts.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 border-t border-border/60 pt-1">
          {parts.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2">
              <span className="tabular-nums text-muted-foreground">{fmtDayMonth(m.expectedDate)}</span>
              <span className="flex items-center gap-1.5">
                <span className="tabular-nums text-foreground/90">{formatEur(m.money.gross)}</span>
                <span
                  className={cn(
                    'rounded px-1 text-[9px] uppercase tracking-wide',
                    partStatus(m) === 'laekunud' || partStatus(m) === 'makstud'
                      ? 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]'
                      : 'bg-foreground/10 text-muted-foreground',
                  )}
                >
                  {partStatus(m)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {remaining > 0 && (
        <div className="mt-1.5 flex justify-between gap-2 border-t border-dashed border-foreground/30 pt-1">
          <span className="text-muted-foreground">Jääk veel jagamata</span>
          <span className="tabular-nums font-semibold text-foreground/90">{formatEur(remaining)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Single-document hover: the full document detail — type, category, deadline,
 * project, certainty, tax declaration (`taxKindOf`), the NET/VAT/GROSS breakdown,
 * and the settlement status. Shown for ungrouped entries.
 */
export function DocHoverCard({
  pos,
  entries,
  today,
}: {
  pos: HoverPos;
  entries: CashFlowEntry[];
  today: string;
  mode: MoneyMode;
}) {
  const e = entries.find((x) => x.id === pos.id);
  if (!e) return null;

  const isIncome = e.kind === 'income';
  const tax = taxKindOf(e);

  const { left, top } = place(pos, 280, 280);

  const Line = ({ k, v }: { k: string; v: ReactNode }) => (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 truncate text-right text-foreground/90">{v}</dd>
    </div>
  );

  return (
    <div
      className="pointer-events-none fixed z-50 w-64 rounded-md border border-border bg-card/95 px-3 py-2 text-[11px] shadow-xl backdrop-blur"
      style={{ left, top }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-1">
        <span className="truncate font-semibold text-foreground">{e.party}</span>
        <KindBadge isIncome={isIncome} />
      </div>
      <dl className="mt-1 space-y-0.5">
        {e.priority && (
          <Line
            k="Prioriteet"
            v={
              <span className={cn('rounded px-1 font-medium', PRIORITY_CLS[e.priority])}>
                {PRIORITY_LABEL[e.priority]}
              </span>
            }
          />
        )}
        {e.docNumber && <Line k="Dok nr" v={<span className="font-mono">{e.docNumber}</span>} />}
        {e.offerNumber && <Line k="Pakkumine" v={<span className="font-mono">{e.offerNumber}</span>} />}
        {e.description && <Line k="Kirjeldus" v={e.description} />}
        {e.docType && <Line k="Liik" v={DOC_TYPE_LABEL[e.docType]} />}
        <Line k="Kategooria" v={LINE_META[e.category].label} />
        {(e.projectName ?? e.project) && (
          <Line
            k="Projekt"
            v={
              <span className="rounded bg-[hsl(var(--primary))]/12 px-1 font-medium text-[hsl(var(--primary))]">
                {e.projectName ?? e.project}
              </span>
            }
          />
        )}
        <Line
          k="Kindlus"
          v={
            <span>
              {CERTAINTY_GLYPH[e.certainty]} {CERTAINTY_LABEL[e.certainty]}
            </span>
          }
        />
        {tax && <Line k="Maksudeklaratsioon" v={tax} />}
      </dl>
      <dl className="mt-1.5 space-y-0.5 border-t border-border/60 pt-1">
        <Line k="Neto" v={<span className="tabular-nums">{formatNumCompact(e.money.net)} €</span>} />
        <Line k={`KM ${e.money.vatRate}%`} v={<span className="tabular-nums">{formatNumCompact(e.money.vat)} €</span>} />
        <div className="flex justify-between gap-3 font-semibold text-foreground">
          <dt>Bruto</dt>
          <dd className="tabular-nums">{formatNumCompact(e.money.gross)} €</dd>
        </div>
      </dl>
      <div className="mt-1.5 border-t border-border/60 pt-1">
        <SettlementDetail view={settlementView(e, today, false)} />
      </div>
      {e.relatedDocuments.length > 0 && (
        <dl className="mt-1.5 space-y-0.5 border-t border-border/60 pt-1">
          <dt className="text-muted-foreground">Seotud dokumendid</dt>
          {relatedDocCells(e).map((c) => (
            <div key={c.linkId} className="flex justify-between gap-2">
              <dd className="min-w-0 truncate text-foreground/90">
                {c.code && <span className="mr-1 font-mono text-muted-foreground">{c.code}</span>}
                {c.docNumber ?? '—'}
              </dd>
              <dd className="shrink-0 tabular-nums text-foreground/90">{formatEur(c.amountCents)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/** One direction's Neto / KM / Bruto triple inside the matrix aggregate card.
 *  The figures are a finance-core `MoneySum` (display only — no component maths;
 *  L9); the accent follows the direction (laekumine = income, makse = cost). */
function MoneyTriple({
  label,
  isIncome,
  sum,
}: {
  label: string;
  isIncome: boolean;
  sum: MoneySum;
}) {
  const accent = isIncome ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]';
  return (
    <div>
      <div className={cn('text-[10px] font-medium uppercase tracking-wide', accent)}>{label}</div>
      <div className="mt-0.5 flex justify-between gap-2 text-[10px]">
        <span className="text-muted-foreground">
          Neto <span className="tabular-nums text-foreground/90">{formatEur(sum.net)}</span>
        </span>
        <span className="text-muted-foreground">
          KM <span className="tabular-nums text-foreground/90">{formatEur(sum.vat)}</span>
        </span>
        <span className="text-muted-foreground">
          Bruto <span className={cn('tabular-nums font-semibold', accent)}>{formatEur(sum.gross)}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * The matrix (table-lens) cell hover: a DELAYED aggregate PREVIEW of exactly the
 * documents the cell's click drills to (the caller resolves that set with the
 * SAME `toRow` + `scope.match` the register uses, so the preview's count and
 * Neto/KM/Bruto reconcile to the register header — L14). It never opens a single
 * document (the calendar's per-chip job); the click stays drill-to-register.
 *
 * Content: the scope label + document count, the leading counterparties (a tally,
 * not money maths), and per-direction Neto/KM/Bruto from `summarizeLedgerScope`
 * (the ONE finance-core fold the register header also uses; L9). An empty set
 * renders nothing — a cell with no documents has no preview.
 */
export function MatrixHoverCard({
  pos,
  label,
  entries,
}: {
  pos: HoverPos;
  label: string;
  entries: CashFlowEntry[];
}) {
  if (entries.length === 0) return null;

  const sum = summarizeLedgerScope(entries);
  const hasIncome = entries.some((e) => e.kind === 'income');
  const hasCost = entries.some((e) => e.kind === 'cost');

  // Leading counterparties by document count — a partition/tally, never arithmetic
  // over money, so this stays inside L9 (the figures above come from finance-core).
  const counts = new Map<string, number>();
  for (const e of entries) counts.set(e.party, (counts.get(e.party) ?? 0) + 1);
  const names = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
  const top = names.slice(0, 3);
  const more = names.length - top.length;

  const { left, top: y } = place(pos, 244, 188);
  return (
    <div
      className="pointer-events-none fixed z-50 w-[15.25rem] rounded-md border border-border bg-card/95 px-3 py-2 text-[11px] shadow-xl backdrop-blur"
      style={{ left, top: y }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-1">
        <span className="truncate font-semibold text-foreground" title={label}>
          {label}
        </span>
        <span className="shrink-0 rounded bg-foreground/10 px-1 text-[9px] font-medium text-muted-foreground">
          {sum.count} dok
        </span>
      </div>
      <div className="mt-1 flex gap-1 text-[10px]">
        <span className="shrink-0 text-muted-foreground">Osapooled:</span>
        <span className="truncate text-foreground/90">
          {top.join(', ')}
          {more > 0 ? ` +${more}` : ''}
        </span>
      </div>
      <div className="mt-1.5 space-y-1.5 border-t border-border/60 pt-1.5">
        {hasIncome && <MoneyTriple label="Laekumine" isIncome sum={sum.income} />}
        {hasCost && <MoneyTriple label="Makse" isIncome={false} sum={sum.cost} />}
      </div>
    </div>
  );
}

/**
 * The pre-computed view model of one past declaration month — built by the
 * shared `kmdMonthStatViews` (kmdScope.ts) from finance-core figures; the
 * card renders it verbatim (no maths in this component).
 */
export interface KmdMonthStatView {
  month: string;
  /** Estonian month label ("märts 2026"). */
  monthLabel: string;
  forecastCents: number;
  actualCents: number | null;
  /** finance-core `kmdVariance(forecast, actual)`; null while unmarked. */
  variance: { diffCents: number; diffPct: number | null } | null;
}

/**
 * KMD backcast hover (1.12): a PAST declaration month's forecast-vs-actual —
 * Prognoos (the document backcast), Tegelik (the marked declaration, or a
 * "märkimata" prompt) and Erinevus as € AND % (finance-core `kmdVariance`
 * output — this card only renders the pre-computed figures; L9). One block
 * per month when a cell spans several declaration deadlines (KOKKU / year).
 */
export function KmdVarianceHoverCard({
  pos,
  stats,
}: {
  pos: HoverPos;
  stats: readonly KmdMonthStatView[];
}) {
  if (stats.length === 0) return null;
  const { left, top } = place(pos, 244, 60 + stats.length * 96);
  return (
    <div
      className="pointer-events-none fixed z-50 w-[15.25rem] rounded-md border border-border bg-card/95 px-3 py-2 text-[11px] shadow-xl backdrop-blur"
      style={{ left, top }}
    >
      <div className="border-b border-border/60 pb-1 font-semibold text-foreground">
        KMD — prognoos vs tegelik
      </div>
      {stats.map((s) => (
        <div key={s.month} className="border-b border-border/40 py-1.5 last:border-b-0 last:pb-0.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.monthLabel}</div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Prognoos (dokumentidest)</span>
            <span className="tabular-nums text-foreground">{formatEur(s.forecastCents)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Tegelik (deklareeritud)</span>
            {s.actualCents === null ? (
              <span className="italic text-muted-foreground">märkimata</span>
            ) : (
              <span className="tabular-nums text-foreground">{formatEur(s.actualCents)}</span>
            )}
          </div>
          {s.variance !== null && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Erinevus</span>
              <span
                className={cn(
                  'tabular-nums',
                  s.variance.diffCents === 0
                    ? 'text-muted-foreground'
                    : s.variance.diffCents > 0
                      ? 'text-[hsl(var(--cost))]'
                      : 'text-[hsl(var(--income))]',
                )}
              >
                {s.variance.diffCents > 0 ? '+' : ''}
                {formatEur(s.variance.diffCents)}
                {s.variance.diffPct !== null &&
                  ` (${s.variance.diffPct > 0 ? '+' : ''}${s.variance.diffPct.toLocaleString('et-EE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%)`}
              </span>
            </div>
          )}
          {s.actualCents === null && (
            <div className="mt-0.5 text-[10px] italic text-muted-foreground">Märgi tegelik ✎ nupust</div>
          )}
        </div>
      ))}
    </div>
  );
}
